import { roundToTwoDecimals } from "@coast/utils"
import {
  DRIVING_MINUTE_PRODUCT_ID,
  OBSERVATION_MINUTE_PRODUCT_ID,
} from "../components/admin/common/constants"
import {
  GQLProductType,
  GQLPurchaseItemInput,
  GQLStudentObject,
} from "../graphqlTypes"
import {
  PriceBreakdown,
  Product,
  PurchaseItem,
  PurchaseLog,
  PurchaseSubItem,
  StateMeetingPoint,
  Subproduct,
  User,
  ProductPackage,
} from "../types"

// See https://gist.github.com/guilhermepontes/17ae0cc71fa2b13ea8c20c94c5c35dc4
export const shuffleArray = <T>(arr: T[]): T[] =>
  arr
    .map((a: any) => [Math.random(), a])
    .sort((a: any, b: any) => a[0] - b[0])
    .map((a: any) => a[1])

export const isValidEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const formatPhoneNumber = (value: string | null | undefined) => {
  if (!value) return value
  // value should never not be a string, but some JS components call this
  // without proper type checking and so it's technically possible.
  const phoneNumber = value.toString().replace(/[^\d]/g, "")
  const phoneNumberLength = phoneNumber.length
  if (phoneNumberLength < 4) return phoneNumber
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
  }
  let phoneNumberNoLeading1 = phoneNumber
  if (phoneNumberNoLeading1.startsWith("1")) {
    phoneNumberNoLeading1 = phoneNumberNoLeading1.slice(1)
  }
  return `(${phoneNumberNoLeading1.slice(0, 3)}) ${phoneNumberNoLeading1.slice(
    3,
    6,
  )}-${phoneNumberNoLeading1.slice(6, 10)}`
}

export const denormalizePhoneNumber = (phoneNumber: string) => {
  const hasCountryCode = phoneNumber.startsWith("+")
  const cleaned = phoneNumber.replace(/\D/g, "")

  let group0: string
  let group1: string
  let group2: string
  let group3: string
  if (hasCountryCode) {
    let offset = Math.max(cleaned.length - 10, 1)
    if (offset > 3) {
      offset = 3
    }
    group0 = `+${cleaned.slice(0, offset)}`
    group1 = cleaned.slice(0 + offset, 3 + offset)
    group2 = cleaned.slice(3 + offset, 6 + offset)
    group3 = cleaned.slice(6 + offset, 10 + offset)
    if (group1) {
      group0 += " "
    }
  } else {
    group0 = ""
    group1 = cleaned.slice(0, 3)
    group2 = cleaned.slice(3, 6)
    group3 = cleaned.slice(6, 10)
  }

  if (group3) {
    return `${group0}(${group1}) ${group2}-${group3}`
  }

  if (group2) {
    return `${group0}(${group1}) ${group2}`
  }

  return `${group0}${group1}`
}

export const normalizePhoneNumber = (phoneNumber: string) => {
  const hasCountryCode = phoneNumber.startsWith("+")
  return `${hasCountryCode ? "+" : ""}${phoneNumber.replace(/\D/g, "")}`
}

export const extractDigits = (phoneNumber: string | undefined | null) =>
  phoneNumber?.replace(/\D/g, "") || ""

export const e164PhoneNumber = (
  phoneNumber: string | number | null | undefined,
) => {
  if (!phoneNumber || typeof phoneNumber === "number") {
    return phoneNumber
  }
  const cleaned = phoneNumber.replace(/\D/g, "")
  if (cleaned.length !== 10) {
    return cleaned
  }
  return `+1${cleaned}`
}

export const saveAs = (uri: string, filename: string) => {
  const link = document.createElement("a")

  if (typeof link.download === "string") {
    link.href = uri
    link.download = filename

    // Firefox requires the link to be in the body
    document.body.appendChild(link)

    // simulate click
    link.click()

    // remove the link when done
    document.body.removeChild(link)
  } else {
    window.open(uri)
  }
}

type NullToUndefinedProps<Base> = NonNullable<{
  [Key in keyof Base]: null extends Base[Key]
    ? Exclude<Base[Key], null> | undefined
    : Base[Key]
}>

export type UndefinedToNullProps<Base> = NonNullable<{
  [Key in keyof Base]: undefined extends Base[Key]
    ? Exclude<Base[Key], undefined> | null
    : Base[Key]
}>

export function convertNullToUndefinedProps<T extends {}>(
  obj: T,
): NullToUndefinedProps<T> {
  const res: any = {}
  const keys = Object.keys(obj) as (keyof T)[]

  keys.forEach((key) => {
    if (obj[key] === null) {
      res[key] = undefined
    } else {
      res[key] = obj[key]
    }
  })

  return res as NullToUndefinedProps<T>
}

export function convertUndefinedToNullProps<T extends {}>(
  obj: T,
): UndefinedToNullProps<T> {
  const res: any = {}
  const keys = Object.keys(obj) as (keyof T)[]

  keys.forEach((key) => {
    if (obj[key] === undefined) {
      res[key] = null
    } else {
      res[key] = obj[key]
    }
  })

  return res as UndefinedToNullProps<T>
}

export const hoursToMinutes = (hours: number) => Math.round(hours * 60)

export const minutesToHours = (minutes: number) =>
  roundToTwoDecimals(minutes / 60)

export const msToHours = (ms: number) => ms * 2.77778e-7

export const hoursToMs = (hours: number) => hours * 3.6e6

export const secondsToHours = (seconds: number) => seconds * 0.000277778

export const hoursToSeconds = (hours: number) => hours * 3600

export const millisToMinutes = (millis: number) => Math.floor(millis / 60000)

export const arrayToCsv = <T extends { toString: () => string }>(data: T[][]) =>
  data
    .map((row: T[]) =>
      row
        .map((v) => v.toString())
        .map((v) => v.replace(new RegExp('"', "g"), '""'))
        .map((v) => `"${v}"`)
        .join(","),
    )
    .join("\r\n")

export const downloadData = (
  content: string,
  filename: string,
  contentType: string,
) => {
  // Create a blob
  const blob = new Blob([content], { type: contentType })
  const url = URL.createObjectURL(blob)

  // Create a link to download it
  const pom = document.createElement("a")
  pom.href = url
  pom.setAttribute("download", filename)
  pom.click()
}

export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const cloudinaryIdFromUrl = (url: string) => {
  /**
   * We save the full Cloudinary URL in the database, but we only want
   * the ID. The ID is the last three parts of the URL, like:
   * instructor-images/portraitImage/some-id.jpg
   */
  const parts = url.split("/")
  return parts.slice(-3).join("/")
}

// Make crypto work on IE.
// @ts-ignore
const crypto = window.crypto || window.msCrypto

export const uuidv4 = () =>
  // @ts-ignore
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => {
    /* eslint-disable-next-line */
    const value = crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4))
    /* eslint-disable-next-line */
    return (c ^ value).toString(16)
  })

/*
 * Returns a Map from an array, the keys of the map are built by getKey function
 * which takes each element in the array to calculate the key.
 * The map is useful to perform fast lookups by key instead of iterating over an array.
 */
export function getMapByKey<T>(
  arr: T[],
  getKey: (next: T) => string,
): Map<string, T> {
  const map = new Map<string, T>()

  arr.forEach((next) => {
    const key = getKey(next)
    map.set(key, next)
  })

  return map
}

export function aggregateNonMinuteProducts(
  list: string,
  item: PurchaseItem,
): string {
  let lines = [list]

  if (item.products) {
    lines = item.products
      .filter(
        (subproduct) =>
          subproduct.id !== DRIVING_MINUTE_PRODUCT_ID &&
          subproduct.id !== OBSERVATION_MINUTE_PRODUCT_ID,
      )
      .map((subproduct) => `(${subproduct.quantity}) ${subproduct.name}`)
  } else if (
    item.type !== GQLProductType.Package &&
    item.id !== DRIVING_MINUTE_PRODUCT_ID &&
    item.id !== OBSERVATION_MINUTE_PRODUCT_ID
  ) {
    lines.push(`(${item.quantity}) ${item.name}`)
  }

  return lines.join(", ")
}

export function aggregateProductPrices(
  total: number,
  item: Product | PurchaseItem,
): number {
  if ("customPrice" in item) {
    return total + (item.customPrice ?? item.price ?? 0) * item.quantity
  }

  return total + (item.price ?? 0) * item.quantity
}

export function aggregateItemMinutes(
  minutes: number,
  item: PurchaseItem | PurchaseSubItem,
): number {
  if ("products" in item && item.products) {
    const subMinutes = item.products?.reduce(aggregateItemMinutes, 0) ?? 0
    return minutes + subMinutes
  }

  if (
    "id" in item &&
    item.id &&
    (item.id === DRIVING_MINUTE_PRODUCT_ID ||
      item.id === OBSERVATION_MINUTE_PRODUCT_ID)
  ) {
    return minutes + item.quantity
  }

  return minutes
}

export function aggregateProductMinutes(
  minutes: number,
  product: Product | Subproduct,
): number {
  if ("products" in product && product.products) {
    const subMinutes = product.products?.reduce(aggregateProductMinutes, 0) ?? 0
    return minutes + subMinutes
  }

  if (
    "productId" in product &&
    product.productId &&
    (product.productId === DRIVING_MINUTE_PRODUCT_ID ||
      product.productId === OBSERVATION_MINUTE_PRODUCT_ID)
  ) {
    return minutes + product.quantity
  }

  if (
    "id" in product &&
    product.id &&
    (product.id === DRIVING_MINUTE_PRODUCT_ID ||
      product.id === OBSERVATION_MINUTE_PRODUCT_ID)
  ) {
    return minutes + product.quantity
  }

  return minutes
}

export function buildAddressLine(
  address: User["address"] | null | undefined,
): string {
  if (!address) {
    return ""
  }

  return [address.address, address.city, address.state, address.zipcode]
    .filter(Boolean)
    .join(", ")
}

export const changeProductCustomPrice = (
  products: Product[],
  itemId: string,
  customPriceString: string,
): Product[] =>
  products.map((product) => {
    if (product.itemId !== itemId) {
      return product
    }

    return {
      ...product,
      customPrice: parseFloat(customPriceString || "0"),
      customPriceString,
    }
  })

export const changeProductQuantities = (
  products: Product[],
  itemId: string,
  subProductId: string | null,
  quantityString: string,
): Product[] => {
  // no subproductId means we are changing the quantity of the product itself
  if (subProductId === null) {
    return products.map((product) => {
      if (product.itemId !== itemId) {
        return product
      }

      return {
        ...product,
        quantity: parseInt(quantityString || "0", 10),
        quantityString,
      }
    })
  }

  // otherwise we are changing the quantity of a subproduct
  return products.map((product) => {
    if (product.itemId !== itemId) {
      return product
    }

    return {
      ...product,
      products: product.products?.map((subProduct) => {
        if (subProduct.productId !== subProductId) {
          return subProduct
        }

        return {
          ...subProduct,
          quantity: parseInt(quantityString || "0", 10),
          quantityString,
        }
      }),
    }
  })
}

export const productsToItemsInput = (products: Product[]): PurchaseItem[] => {
  // Product is the form state for a product in a purchase log
  // users can specify quantities and prices for each, so we want to
  // transform and filter them into valid items to submit for a refund or transfer
  const allProducts = products.map((product) => ({
    id: product.id,
    itemId: product.itemId,
    name: product.name,
    type: product.type as GQLProductType,
    priceSchemePrice: product.customPrice ?? 0,
    price: product.customPrice ?? 0,
    quantity: product.quantity,
    products:
      product.products
        ?.map((subProduct) => ({
          id: subProduct.productId,
          itemId: subProduct.itemId,
          type: subProduct.type as GQLProductType,
          name: subProduct.productName,
          quantity: subProduct.quantity,
          basePrice: 0,
        }))
        // only keep the subproducts that have a set quantity (negative or positive)
        .filter((subProduct) => subProduct.quantity) ?? null,
  }))

  // keep only valid products
  return allProducts.filter((product) => {
    const isPackageWithSubProducts = Boolean(product.products?.length)
    const isPackageWithPrice =
      product.type === GQLProductType.Package && product.price !== 0
    const isProductWithQuantity =
      product.type !== GQLProductType.Package && product.quantity > 0

    return (
      isPackageWithSubProducts || isPackageWithPrice || isProductWithQuantity
    )
  })
}

export function getProductsFromLog(log: PurchaseLog): Product[] {
  // convert purchase item to form product
  if (log.items) {
    return log.items.map((item) => ({
      itemId: item.itemId,
      id: item.id,
      name: item.name,
      type: item.type,
      quantity: item.quantity,
      quantityString: item.quantity.toString(),
      availableQuantity: item.quantity,
      customPriceString: item.price.toString(),
      customPrice: item.price,
      availableAmount: item.price,
      price: item.price,
      products: item.products?.map((subProduct) => ({
        itemId: subProduct.itemId,
        type: subProduct.type,
        productId: subProduct.id,
        productName: subProduct.name,
        quantity: subProduct.quantity,
        quantityString: subProduct.quantity.toString(),
        availableQuantity: subProduct.quantity,
      })),
    }))
  }

  // convert log without items to log with items
  if (log.minutesPurchased) {
    return [
      {
        itemId: uuidv4(),
        id: "custom-package",
        name: log.description,
        type: GQLProductType.Package,
        quantity: 1,
        quantityString: "1",
        availableQuantity: 1,
        customPriceString: log.amountDollars.toString(),
        customPrice: log.amountDollars,
        availableAmount: log.amountDollars,
        price: log.amountDollars,
        products: [
          {
            itemId: uuidv4(),
            type: GQLProductType.DrivingMinute,
            productId: DRIVING_MINUTE_PRODUCT_ID,
            productName: "Driving Minute",
            quantity: log.minutesPurchased,
            quantityString: log.minutesPurchased.toString(),
            availableQuantity: log.minutesPurchased,
          },
        ],
      },
    ]
  }

  // other products like change fee, shipping fee, course, etc
  // we don't have a way to handle these yet until the ledger migration completes
  // so we add a custom product that has name = description and the total is the price
  return [
    {
      itemId: uuidv4(),
      id: "custom-product",
      name: log.description,
      type: GQLProductType.Fee,
      quantity: 1,
      quantityString: "1",
      availableQuantity: 1,
      customPriceString: log.amountDollars.toString(),
      customPrice: log.amountDollars,
      availableAmount: log.amountDollars,
      price: log.amountDollars,
    },
  ]
}

export function getItemsFromPackage(
  pack: ProductPackage,
  priceBreakdown: PriceBreakdown | undefined,
): GQLPurchaseItemInput[] {
  if (
    pack.id === "standard-2" &&
    priceBreakdown &&
    priceBreakdown.drivingMinutesUsed > 0
  ) {
    return [
      {
        itemId: uuidv4(),
        productId: DRIVING_MINUTE_PRODUCT_ID,
        quantity: 120 - priceBreakdown.drivingMinutesUsed,
      },
    ]
  }

  return [
    {
      itemId: uuidv4(),
      productId: pack.id,
      quantity: 1,
    },
  ]
}

// source https://gist.github.com/solenoid/1372386
export function mongoObjectId(): string {
  // eslint-disable-next-line no-bitwise
  const timestamp = ((Date.now() / 1000) | 0).toString(16)

  return (
    timestamp +
    "xxxxxxxxxxxxxxxx"
      // eslint-disable-next-line no-bitwise
      .replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
      .toLowerCase()
  )
}

export function flattenItemsByQuantity(items: PurchaseItem[]): PurchaseItem[] {
  return items.flatMap((item) => {
    if (item.quantity === 1 || item.type !== GQLProductType.Package) {
      return item
    }

    return Array.from({ length: item.quantity }).map(() => ({
      ...item,
      itemId: uuidv4(),
      quantity: 1,
    }))
  })
}

export const combineNameParts = (nameParts: {
  firstName?: string
  lastName?: string
  middleName?: string
  middleInit?: string
}) =>
  [
    nameParts.firstName,
    nameParts.middleName || nameParts.middleInit,
    nameParts.lastName,
  ]
    .filter((v) => !!v)
    .join(" ")

export const getUserNameParts = (userName: string) => {
  const nameArr = userName.split(" ")
  if (nameArr.length === 1) {
    return {
      firstName: userName,
      middleName: "",
      middleInit: "",
      lastName: "",
    }
  }
  if (nameArr.length === 2) {
    return {
      firstName: nameArr[0],
      middleName: "",
      middleInit: "",
      lastName: nameArr[1],
    }
  }
  return {
    firstName: nameArr[0],
    middleName: nameArr[1],
    middleInit: nameArr[1][0],
    lastName: nameArr[2],
  }
}

export const convertNameToLastFirst = (userName: string) => {
  const nameParts = userName.split(" ")
  const lastName = nameParts.pop()
  const firstNames = nameParts.join(" ")
  return { lastName, firstNames }
}

export const sortStudentsByLastName = (students: GQLStudentObject[]) => {
  const studentsByLastName = students?.map((student) => {
    const { firstNames, lastName } = convertNameToLastFirst(student.name)
    return {
      ...student,
      name: `${lastName}, ${firstNames}`,
    }
  })

  studentsByLastName?.sort((a, b) => a.name.localeCompare(b.name))
  return studentsByLastName ?? []
}

export const pluralize = (singularWord: string, num: number) =>
  `${singularWord}${num === 1 ? "" : "s"}`

export const renderMeetingPointDetails = (meetingPoint: StateMeetingPoint) =>
  `${[meetingPoint.label, "located at", meetingPoint.address, meetingPoint.description].filter((v) => !!v).join(" ")}.`

export async function copyTextToClipboard(text: string) {
  if ("clipboard" in navigator) {
    await navigator.clipboard.writeText(text)
    return true
  }
  return false
}

export function removeNullish<T>(arr: T[]): NonNullable<T>[] {
  return arr.filter(
    (item) => item !== null && item !== undefined,
  ) as NonNullable<T>[]
}

export function removeNullishFromObject<T extends {}>(obj: T): NonNullable<T> {
  const res: any = {}
  const keys = Object.keys(obj) as (keyof T)[]

  keys.forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined) {
      res[key] = obj[key]
    }
  })

  return res as NonNullable<T>
}

export function metersToMiles(meters: number): number {
  return meters * 0.000621371
}
